import React, { Fragment, useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';

import ActualVsBudgetReportDetailedTableRow from './ActualVsBudgetReportDetailedTableRow';
import ActualVsBudgetReportHeader from './ActualVsBudgetReportHeader';
import ActualVsBudgetReportLegend from './ActualVsBudgetReportLegend';
import ActualVsBudgetReportTransactionsModal from './ActualVsBudgetReportTransactionsModal';
import { useActualVsBudgetReportContext } from './context/ActualVsBudgetReportContext';
import { buildGroupedHeaderRows } from './helpers/reportHeaderGroups';
import { GROUP_START_CELL, reportNegativeColourClass } from './helpers/reportRowStyles';
import { resolveVersionNames } from './helpers/reportVersionNames';
import { varianceColourClass } from './helpers/varianceColour';
import { ReportColumnHeaderRow, ReportGroupHeaderRow } from './ReportTableHeaderRows';
import { TYPE_DEPARTMENT } from './types/ModuleType';
import { COL_COMMITTED, COL_FORECAST, COL_MONTHLY_PHASING, COL_OPPORTUNITY, COL_PERCENT, COL_RISK, COL_UNCOMMITTED } from './types/ReportColumns';
import { ROW_BUDGET_LINE, ROW_COST_CENTRE, ROW_PROJECT, ROW_TOTAL } from './types/ReportRowType';
import { NOT_IN_CURRENT_STRUCTURE_ID } from './types/ReportType';

import { formatDateWithTz } from 'Helpers/dateUtils';
import { formatFigure, formatNegativeMoney } from 'Helpers/formatService';
import { generateKey } from 'Helpers/misc';
import { modalOptions } from 'Helpers/modalOptions';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import TableFilterModal from 'CommonComponents/TableFilterModal';

import useModal from 'Hooks/useModal';

// A zero renders as the zero dash; negatives stay in parentheses.
const formatReportFigure = (value) => formatFigure(value, formatNegativeMoney);

const DepartmentActualVsBudgetReportDetailedTable = ({ Actions }) => {
    const [displayDates, setDisplayDates] = useState({});
    const [currentTransactions, setCurrentTransactions] = useState([]);

    const context = useActualVsBudgetReportContext();
    const { module, details, expanded, toggleExpansion, toggleAllExpansion } = context;
    const transactionModal = useModal({ options: { ...modalOptions(true), backdrop: 'dynamic' } });

    // Column visibility, kept in lock-step with the row component so the header
    // and the cells can never disagree about which columns are rendered.
    const columns = context.visibleColumns || {};
    const monthlyPhasing = !!columns[COL_MONTHLY_PHASING];
    const supplementaryKeys = [COL_COMMITTED, COL_UNCOMMITTED, COL_PERCENT, COL_RISK, COL_OPPORTUNITY, COL_FORECAST];
    const datesArray = details?.datesArray || [];
    const displayedMonths = monthlyPhasing ? datesArray.filter((month) => displayDates[month]) : [];
    const firstVisibleSupplementary = supplementaryKeys.find((key) => columns[key]);
    const { versionName } = resolveVersionNames(context);

    // Grand-total footer cell — dark fill, so negatives take the lighter red tint.
    const footerFigure = (value, groupStart = false) => (
        <td className={classNames('inbud-numeric-cell', reportNegativeColourClass(value, ROW_TOTAL), { [GROUP_START_CELL]: groupStart })}>
            {formatReportFigure(value)}
        </td>
    );

    // A department / cost-centre report totals expenditure only, so an underspend
    // is always the favourable direction here — unlike the event report, whose
    // bottom line is net.
    const footerVariance = (value, groupStart = false) => (
        <td className={classNames('inbud-numeric-cell', varianceColourClass(value, false, true), { [GROUP_START_CELL]: groupStart })}>
            {formatReportFigure(value)}
        </td>
    );

    useEffect(() => {
        toggleDisplayDates();
    }, [details]);

    // A comparison version's unmatched lines are filed under a synthetic sentinel
    // department; this page reports on one real department, so it must never be
    // picked up as that department.
    const realDepartmentKeys = (data) => Object.keys(data?.expense?.departments || {}).filter((name) => name !== NOT_IN_CURRENT_STRUCTURE_ID);

    const departmentKey = useMemo(() => {
        return realDepartmentKeys(details?.data)[0];
    }, [details]);

    const departmentData = useMemo(() => {
        const key = realDepartmentKeys(details?.data)[0];

        return details?.data?.expense?.departments?.[key] || null;
    }, [details]);

    const costCenterKey = useMemo(() => {
        return Object.keys(departmentData?.costcenters || {})[0];
    }, [departmentData]);

    const costcenterData = useMemo(() => {
        return departmentData?.costcenters?.[costCenterKey] || null;
    }, [departmentData, costCenterKey]);

    const data = useMemo(() => {
        return module === TYPE_DEPARTMENT ? departmentData : costcenterData;
    }, [costcenterData, departmentData]);

    // Two-row grouped header, built from the same helper as the event-level
    // report so the two views stay in step column for column.
    const headerRows = useMemo(() => {
        if (!details?.headings) {
            return { groups: [], columns: [] };
        }

        return buildGroupedHeaderRows({
            supplementaryKeys,
            visibleColumns: columns,
            months: datesArray,
            displayedMonths,
            rowLabel: $t('Cost Centre / Project / Budget Line'),
        });
    }, [details, displayDates, columns, monthlyPhasing]);

    const dates = useMemo(() => {
        return (details?.datesArray || []).reduce((result, dateString) => {
            const date = moment(dateString, 'MM-YYYY');
            const year = date.year();

            if (!result[year]) {
                result[year] = { months: [] };
            }

            result[year].months.push({ label: date.format('MMM'), value: dateString });

            return result;
        }, {});
    }, [details]);

    const toggleDisplayDates = (value = true) => {
        const result = (details?.datesArray || []).reduce((result, dateString) => {
            result[dateString] = value;
            return result;
        }, {});

        setDisplayDates(result);
    };

    const toggleDisplayDate = (key) => {
        setDisplayDates((dates) => ({ ...dates, [key]: !dates[key] }));
    };

    const dataClickHandle = (data, month) => {
        if (month && data?.length) {
            data = filterTransactionsByMonth(data, month);
        }

        setCurrentTransactions(data || []);
        transactionModal.show();
    };

    const filterTransactionsByMonth = (data, month) => {
        const formattedMonth = formatDateWithTz(month, 'MM-YYYY', 'YYYY-MM');
        return data.filter((item) => item.date.includes(formattedMonth));
    };

    return (
        <div>
            {/* This level renders no comparison columns, so it advertises no
                comparison either — see the header's showComparisonChip note. */}
            <ActualVsBudgetReportHeader showComparisonChip={false} />
            <div className="tw-mt-4 tw-flex tw-gap-x-4 tw-justify-between tw-items-end tw-mb-4">
                <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                    <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</div>
                    <div className="tw-flex tw-gap-x-1.5">
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllExpansion()}>
                            {$t('Expand all')}
                        </button>
                        <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={() => toggleAllExpansion(false)}>
                            {$t('Collapse all')}
                        </button>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-x-2">
                    <div>{Actions}</div>
                    <TableFilterModal>
                        <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                            <div className="tw-text-xs tw-text-charcoal">{$t('Display Months')}</div>
                            <div className="tw-space-x-2">
                                <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => toggleDisplayDates()}>
                                    {$t('Select all')}
                                </button>
                                <button className="tw-text-xs tw-font-semibold tw-underline" onClick={(e) => toggleDisplayDates(false)}>
                                    {$t('Unselect all')}
                                </button>
                            </div>
                        </div>
                        <div className="tw-space-y-3">
                            {Object.entries(dates).map(([year, yearData]) => (
                                <div key={year} className="tw-space-y-1">
                                    <div className="tw-text-xs tw-font-medium">{year}</div>
                                    <div className="tw-grid tw-grid-cols-6 tw-gap-x-3 tw-gap-y-1">
                                        {yearData.months.map((month) => (
                                            <CheckboxInput
                                                key={month.value}
                                                id={month.value}
                                                label={month.label}
                                                checked={displayDates[month.value] || false}
                                                onChange={() => toggleDisplayDate(month.value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </TableFilterModal>
                </div>
            </div>
            {departmentData && (
                <div className="inbud-table_wrapper avb-report-wrapper tw-overflow-y-auto tw-max-h-screen" id="actualCostDetailed">
                    {/* `avb-report-table` is what tables/_avb-report.scss keys on —
                        without it this table falls back to the shared dense grid in
                        tables/_style.scss (grey striping, 2px rules). */}
                    <table className="inbud-table avb-report-table inbud-table_first-sticky">
                        <thead className="tw-sticky tw-top-0 tw-z-10">
                            <ReportGroupHeaderRow groups={headerRows.groups} />
                            <ReportColumnHeaderRow columns={headerRows.columns} />
                        </thead>
                        <tbody>
                            {Object.entries(departmentData.costcenters).map(([costcenterName, costcenterData]) => {
                                const workforceCCData = costcenterData.workforce ?? null;
                                const projectsCcData = costcenterData.projects && _.isObject(costcenterData.projects) ? costcenterData.projects : null;

                                return (
                                    <Fragment key={costcenterName}>
                                        {module === TYPE_DEPARTMENT && (
                                            <ActualVsBudgetReportDetailedTableRow
                                                onDataClick={(month) => dataClickHandle(costcenterData.source, month)}
                                                key={generateKey()}
                                                rowType={ROW_COST_CENTRE}
                                                favourableWhenNegative={false}
                                                data={costcenterData.total}
                                                displayDates={displayDates}
                                                show={true}
                                                toggleHandler={() => toggleExpansion('expense', departmentKey, costcenterName)}
                                                expandable={true}
                                                expanded={expanded.expense[departmentKey].costcenters[costcenterName].expanded}
                                                actualData={costcenterData.source || []}
                                            />
                                        )}
                                        {Object.keys(costcenterData.codes.data).map((code) => {
                                            return (
                                                <ActualVsBudgetReportDetailedTableRow
                                                    onDataClick={(month) => dataClickHandle(costcenterData.codes.source[code], month)}
                                                    key={generateKey()}
                                                    rowType={ROW_BUDGET_LINE}
                                                    favourableWhenNegative={false}
                                                    data={costcenterData.codes.data[code]}
                                                    displayDates={displayDates}
                                                    level={module === TYPE_DEPARTMENT ? 2 : 1}
                                                    show={
                                                        module === TYPE_DEPARTMENT ? expanded.expense[departmentKey].costcenters[costcenterName].expanded : true
                                                    }
                                                    toggleHandler={() => toggleExpansion('expense', departmentKey, costcenterName, code)}
                                                    expandable={false}
                                                    expanded={expanded.expense[departmentKey].costcenters[costcenterName].codes[code].expanded}
                                                    actualData={costcenterData.codes.source[code] || []}
                                                    tooltipId={generateKey()}
                                                    isInternalRow={true}
                                                />
                                            );
                                        })}

                                        {!!projectsCcData && (
                                            <>
                                                {Object.keys(projectsCcData).map((ccProject) => {
                                                    const ccProjectData = projectsCcData[ccProject];

                                                    return (
                                                        <Fragment key={ccProject}>
                                                            <ActualVsBudgetReportDetailedTableRow
                                                                onDataClick={(month) => dataClickHandle(ccProjectData.source, month)}
                                                                key={generateKey()}
                                                                rowType={ROW_PROJECT}
                                                                favourableWhenNegative={false}
                                                                data={ccProjectData.total}
                                                                displayDates={displayDates}
                                                                level={module === TYPE_DEPARTMENT ? 2 : 1}
                                                                show={
                                                                    module === TYPE_DEPARTMENT
                                                                        ? expanded.expense[departmentKey].costcenters[costcenterName].expanded
                                                                        : true
                                                                }
                                                                toggleHandler={() =>
                                                                    toggleExpansion('expense', departmentKey, costcenterName, null, null, ccProject)
                                                                }
                                                                expandable={true}
                                                                expanded={
                                                                    expanded.expense[departmentKey].costcenters[costcenterName].projects[ccProject].expanded
                                                                }
                                                                actualData={ccProjectData.source || []}
                                                                tooltipId={generateKey()}
                                                            />
                                                            {Object.keys(ccProjectData.codes.data).map((code) => {
                                                                return (
                                                                    <ActualVsBudgetReportDetailedTableRow
                                                                        onDataClick={(month) => dataClickHandle(ccProjectData.codes.source[code], month)}
                                                                        key={generateKey()}
                                                                        rowType={ROW_BUDGET_LINE}
                                                                        favourableWhenNegative={false}
                                                                        data={ccProjectData.codes.data[code]}
                                                                        displayDates={displayDates}
                                                                        level={3}
                                                                        show={
                                                                            module === TYPE_DEPARTMENT
                                                                                ? expanded.expense[departmentKey].costcenters[costcenterName].expanded &&
                                                                                  expanded.expense[departmentKey].costcenters[costcenterName].projects[
                                                                                      ccProject
                                                                                  ].expanded
                                                                                : expanded.expense[departmentKey].costcenters[costcenterName].projects[
                                                                                      ccProject
                                                                                  ].expanded
                                                                        }
                                                                        toggleHandler={() =>
                                                                            toggleExpansion('expense', departmentKey, costcenterName, code, null, ccProject)
                                                                        }
                                                                        expandable={false}
                                                                        expanded={true}
                                                                        actualData={ccProjectData.codes.source[code] || []}
                                                                        tooltipId={generateKey()}
                                                                        isInternalRow={true}
                                                                    />
                                                                );
                                                            })}
                                                        </Fragment>
                                                    );
                                                })}
                                            </>
                                        )}

                                        {!!workforceCCData && (
                                            <>
                                                <ActualVsBudgetReportDetailedTableRow
                                                    onDataClick={(month) => dataClickHandle(workforceCCData.source, month)}
                                                    key={generateKey()}
                                                    rowType={ROW_PROJECT}
                                                    favourableWhenNegative={false}
                                                    data={workforceCCData.total}
                                                    displayDates={displayDates}
                                                    level={module === TYPE_DEPARTMENT ? 2 : 1}
                                                    show={
                                                        module === TYPE_DEPARTMENT ? expanded.expense[departmentKey].costcenters[costcenterName].expanded : true
                                                    }
                                                    toggleHandler={() => toggleExpansion('expense', departmentKey, costcenterName, null, true)}
                                                    expandable={true}
                                                    expanded={expanded.expense[departmentKey].costcenters[costcenterName].workforce.expanded}
                                                    actualData={workforceCCData.source || []}
                                                    tooltipId={generateKey()}
                                                    isInternalRow={true}
                                                />

                                                {Object.keys(workforceCCData.codes.data).map((code) => {
                                                    return (
                                                        <ActualVsBudgetReportDetailedTableRow
                                                            onDataClick={(month) => dataClickHandle(workforceCCData.codes.source[code], month)}
                                                            key={generateKey()}
                                                            rowType={ROW_BUDGET_LINE}
                                                            favourableWhenNegative={false}
                                                            data={workforceCCData.codes.data[code]}
                                                            displayDates={displayDates}
                                                            level={module === TYPE_DEPARTMENT ? 3 : 2}
                                                            show={
                                                                module === TYPE_DEPARTMENT
                                                                    ? expanded.expense[departmentKey].costcenters[costcenterName].expanded &&
                                                                      expanded.expense[departmentKey].costcenters[costcenterName].workforce.expanded
                                                                    : expanded.expense[departmentKey].costcenters[costcenterName].workforce.expanded
                                                            }
                                                            toggleHandler={() => toggleExpansion('expense', departmentKey, costcenterName, code)}
                                                            expandable={false}
                                                            expanded={false}
                                                            actualData={workforceCCData.codes.source[code] || []}
                                                            tooltipId={generateKey()}
                                                            isInternalRow={true}
                                                        />
                                                    );
                                                })}
                                            </>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot className="tw-text-xs [&_td]:!tw-font-normal [&_td]:!tw-bg-dark-blue">
                            {'total' in data && (
                                <tr>
                                    <td>{$t('Total')}</td>
                                    {monthlyPhasing &&
                                        datesArray.map((month) => {
                                            const id = month;
                                            return (
                                                <td className={classNames('inbud-numeric-cell', { 'tw-hidden': !displayDates[month] })} key={id}>
                                                    {formatReportFigure(data.total.includes[month]?.actual_total ?? 0)}
                                                </td>
                                            );
                                        })}
                                    {footerFigure(data.total.month_budget_total, true)}
                                    {footerFigure(data.total.month_actual_total)}
                                    {footerVariance(data.total.month_variance)}

                                    {footerFigure(data.total.period_budget_total, true)}
                                    {footerFigure(data.total.period_actual_total)}
                                    {footerVariance(data.total.period_variance)}

                                    {footerFigure(data.total.budget_total, true)}
                                    {footerFigure(data.total.actual_total)}
                                    {footerVariance(data.total.total_variance)}

                                    {columns[COL_COMMITTED] && footerFigure(data.total.committed_total, firstVisibleSupplementary === COL_COMMITTED)}
                                    {columns[COL_UNCOMMITTED] && footerFigure(data.total.uncommitted_total, firstVisibleSupplementary === COL_UNCOMMITTED)}
                                    {columns[COL_PERCENT] && footerFigure(data.total.uncommitted_percent, firstVisibleSupplementary === COL_PERCENT)}
                                    {columns[COL_RISK] && footerFigure(data.total.risk, firstVisibleSupplementary === COL_RISK)}
                                    {columns[COL_OPPORTUNITY] && footerFigure(data.total.opp, firstVisibleSupplementary === COL_OPPORTUNITY)}
                                    {columns[COL_FORECAST] && footerFigure(data.total.afc, firstVisibleSupplementary === COL_FORECAST)}
                                </tr>
                            )}
                        </tfoot>
                    </table>
                </div>
            )}
            <ActualVsBudgetReportLegend versionName={versionName} comparisonName="" />
            <ActualVsBudgetReportTransactionsModal modal={transactionModal} transactions={currentTransactions} />
        </div>
    );
};

export default DepartmentActualVsBudgetReportDetailedTable;
